(ns app.front.ui.rich-text
  (:require [app.front.entities :as entities]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.tiptap :as tiptap]
            [app.front.util :as futil]
            [app.rich-text :as rich-text]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn editor
  "Create a rich text editor. The input is not controlled (i.e. the content is
  only read from `:initial-value` once), but a new string representing
  the content will be given in on-change and on-blur.

  If no `on-change` or `on-blur` function is provided, will render a read-only viewer.

  If `:authority-group-id` is provided, will include a phrase selector. Phrases may also be passed as an argument for form support.

  `:button-style` is a CSS class that is given for the editor buttons

  `:code-reference-fn` can be given if the selected code reference needs to be passed from the model code selector when inserting the code text.

   `:placeholder` is the gray text shown when content is otherwise empty

   `:template-placeholders` is a collection of placeholders, given as maps with an `:id` key. They will have special
   rendering if used like `%my-placeholder%`

   `:disable-read-more?` renders read-only content at its full height without a Show more/less toggle."
  [{:keys [authority-group-id id label-text label-class required? phrases placeholder tr] :as args}]
  (let [tr          (or tr (<sub [:tr]))
        phrases     (or phrases
                        (<sub [::entities/entities {:entity/type            :entity.type/phrase
                                                    :domain/authority-group authority-group-id}]))
        placeholder (some->> placeholder (conj []) tr)
        rte-key     (str "rte-" id)]
    [eb/error-boundary
     [:div.w-full.flex.flex-col
      (when label-text
        [:label.bx--label.flex-1
         {:class (str (when required? "required-field ")
                      label-class)
          :for   id}
         label-text])
      [tiptap/editor-impl (assoc args :tr tr :phrases phrases :editor editor :key rte-key :placeholder placeholder)]]]))

; The original implementation of the editor didn't coerce the value it yields
; to the on-change handler to an empty string when the editor is empty. It yielded "<p></p>".
; Going forward, use `editor` instead, `editor-legacy` will be deprecated.
; Pass :dont-coerce-to-empty-string? to `editor` explicitly if that is what you need.
(defn editor-legacy [args]
  (editor (assoc args :dont-coerce-to-empty-string? true)))

(defn empty-texts? [value]
  (try
    (-> (.parse js/JSON value)
        (js->clj :keywordize-keys true)
        :blocks
        (->> (mapcat :text))
        empty?)
    (catch js/Error _
      ;; Might be HTML
      (util/nil-or-blank? value))))

(defn- expand-link
  [*expanded tr]
  (let [expanded? @*expanded]
    (cc/link {:on-click   #(do (.stopPropagation %) (swap! *expanded not))
              :class-name "cursor-pointer"}
             (str " " (if expanded?
                        (tr [:show-less])
                        (tr [:show-more]))))))
(defn expandable-text
  [{:keys [id text max-len tr rt-enabled?]}]
  (r/with-let [*expanded (r/atom false)]
    (let [plaintext (rich-text/join-rich-text-content text)
          too-long? (< max-len (count plaintext))
          rt-viewer (editor-legacy (util/mmap {:initial-value text}
                                              id))]
      (cond
        (and too-long? @*expanded)       [:div
                                          (if rt-enabled? rt-viewer plaintext)
                                          [expand-link *expanded tr]]
        (and too-long? (not @*expanded)) [:div
                                          [futil/ellipsis plaintext max-len]
                                          [expand-link *expanded tr]]
        :else                            [:div
                                          (if (and rt-enabled? (str-util/blank->nil plaintext))
                                            rt-viewer
                                            plaintext)]))))
